import React, { useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from 'react-apollo';

import ContextualString from '../../../../../../../../../components/contextual/ContextualString';
import { AlbumSelectedIdentitiesForFaceTagging } from '../../../../../../FaceTagPhoto/graphql/queries';
import Spinner from '../../../../../../../../../components/global/Spinner';
import useDebounce from '../../../../../../../../../hooks/useDebounce';
import FormGroup from '../../../../../../../../../components/global/FormGroup';
import { ALBUM_MANUAL_MATCHING_IDENTITY_FILTERS } from '../../constants';
import useSliceFilterOpts from '../../../../../../../../../components/product/hooks/useSliceFilterOpts';
import Button from '../../../../../../../../../components/global/Button';
import Angle from '../../../../../../../../../components/global/Angle';
import DropdownMenu from '../../../../../../../../../components/global/DropdownMenu';
import IdentitySelectorOptionDraggable from '../IdentitySelectorOptionDraggable';

const propTypes = {
  album: PropTypes.shape({
    id: PropTypes.string,
  }),
  onIdentitiesLoaded: PropTypes.func,
};

const IdentitySelector = ({ album, onIdentitiesLoaded }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery] = useDebounce(searchQuery, 300);
  const { filter, onFilterUpdate } = useSliceFilterOpts({
    initialFilter: {
      identityFilter: ALBUM_MANUAL_MATCHING_IDENTITY_FILTERS.ALL,
      offset: 0,
      limit: 50,
    },
  });

  const { loading, data: { album: albumWithIdenities } = {} } = useQuery(
    AlbumSelectedIdentitiesForFaceTagging,
    {
      variables: {
        id: album?.id,
        limit: filter.limit,
        searchQuery: debouncedSearchQuery,
        hasMatchedPhotos:
          filter.identityFilter ===
          ALBUM_MANUAL_MATCHING_IDENTITY_FILTERS.WITHOUT_MATCHES
            ? false
            : undefined,
      },
      skip: !album?.id,
      fetchPolicy: 'network-only',
    },
  );
  const identities = useMemo(
    () =>
      (albumWithIdenities?.selected_identities?.records ?? [])
        .filter(
          // Avoid allowing to select users with no faces. No votes should be casted on these.
          ({ identity }) =>
            !!identity?.current_face?.id ||
            !!identity?.current_matching_face?.id,
        )
        .map(({ identity }) => identity),
    [albumWithIdenities?.selected_identities?.records],
  );

  useEffect(() => {
    if (onIdentitiesLoaded) {
      onIdentitiesLoaded(identities);
    }
  }, [identities]);

  const membersLabel = useMemo(
    () => (
      <ContextualString
        id="albumHeaderMembersLabel"
        defaultMessage="members"
        type={album?.album_type}
        subtype={album?.album_subtype}
      />
    ),
    [album],
  );

  return (
    <div className="manual-matching-identity-selector">
      <div>
        <h4 className="mb-1">{membersLabel}</h4>
        <p className="text-xs">
          Simply drag-and-drop a member over a photo to add their face tags.
          Drag and drop will always tag the largest face in the photo. If you
          are trying to tag a smaller face, you must click into the photo and
          manually apply it.
        </p>
        <div className="d-flex">
          <FormGroup
            className="mb-2 w-100"
            id="identity-search"
            name="searchQuery"
            label="Search"
            labelSrOnly
            placeholder=" Search..."
            value={searchQuery}
            onChange={({ target }) => setSearchQuery(target.value)}
          />
        </div>
        <p className="text-xs">
          Note: {membersLabel} with no available selfies will not be displayed.
        </p>
        <DropdownMenu
          closeOnClickWithin
          toggle={
            <div className="text-bold text-xs">
              {filter.identityFilter} <Angle direction="down" />
            </div>
          }
        >
          {Object.values(ALBUM_MANUAL_MATCHING_IDENTITY_FILTERS).map(value => (
            <Button
              key={value}
              btnSize="sm"
              btnStyle="reset"
              className="dropdown-item"
              onClick={() =>
                onFilterUpdate({ identityFilter: value, offset: 0 })
              }
            >
              {value}
            </Button>
          ))}
        </DropdownMenu>
      </div>
      <div className="manual-matching-identity-selector-identities">
        {loading && (
          <div className="text-center">
            <Spinner />
          </div>
        )}
        {!loading && identities.length > 0 && (
          <div>
            {identities.map(identity => (
              <IdentitySelectorOptionDraggable
                key={identity.id}
                identity={identity}
              />
            ))}
          </div>
        )}
        {!loading && identities.length === 0 && (
          <div className="text-center text-sm text-muted">
            No members found.
          </div>
        )}
      </div>
    </div>
  );
};

IdentitySelector.propTypes = propTypes;

export default IdentitySelector;
